import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useTranslation } from "@/i18n/translations";
import { useNavigate } from "react-router-dom";
import AnimatedGradientBackground from "@/components/AnimatedGradientBackground";
import { Eye, EyeOff } from "lucide-react";

const RECAPTCHA_SITE_KEY = "6LdY0Q4sAAAAAKJebMA-sLo9G-jlCN4YIQhi5gdw";

export default function AuthPage({ setIsAuthenticated }) {
  const { t, lang, setLanguage } = useTranslation();
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(() => {
    try { return !new URLSearchParams(window.location.search).get('promo'); } catch (e) { return true; }
  });
  const [pendingPromo] = useState(() => {
    try {
      const p = new URLSearchParams(window.location.search).get('promo');
      if (p) {
        const up = p.trim().toUpperCase();
        localStorage.setItem('chatity_promo', up);
        return up;
      }
      return localStorage.getItem('chatity_promo') || "";
    } catch (e) { return ""; }
  });
  const getStoredRefCode = () => {
    try {
      const urlRef = new URLSearchParams(window.location.search).get('ref');
      if (urlRef) return urlRef.trim().toLowerCase();
      const lsRef = localStorage.getItem('chatity_ref');
      if (lsRef) return lsRef;
      const cookieRef = document.cookie.split('; ').find((r) => r.startsWith('chatity_ref='));
      if (cookieRef) return decodeURIComponent(cookieRef.split('=')[1] || '');
    } catch (e) { /* noop */ }
    return "";
  };

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    salon_name: "",
    website_url: "",
    referral_code: getStoredRefCode(),
    terms_accepted: false
  });
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginMode, setLoginMode] = useState('code');
  const [codeSent, setCodeSent] = useState(false);
  const [loginCode, setLoginCode] = useState('');

  // Load reCAPTCHA script only on this page
  useEffect(() => {
    // Check if script already loaded
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }

    // Create and load reCAPTCHA script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      setRecaptchaLoaded(true);
    };
    
    script.onerror = () => {
      console.error('Failed to load reCAPTCHA');
      setRecaptchaLoaded(false);
    };

    document.head.appendChild(script);

    // Cleanup: remove badge when leaving page
    return () => {
      // Hide reCAPTCHA badge when leaving auth page
      const badge = document.querySelector('.grecaptcha-badge');
      if (badge) {
        badge.style.visibility = 'hidden';
      }
    };
  }, []);

  const handleRequestCode = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-login-code`, {
        email: formData.email,
        origin_url: window.location.origin
      });
      setCodeSent(true);
      toast.success(t('auth.codeSentInfo') || 'Код отправлен на вашу почту');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-login-code`, {
        email: formData.email,
        code: loginCode
      });
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      toast.success(t('auth.loginSuccess'));
      setIsAuthenticated(true);
      navigate(response.data.user.is_admin === true ? '/admin/dashboard' : '/calendar');
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLogin && loginMode === 'code') {
      if (!codeSent) {
        await handleRequestCode();
      } else {
        await handleVerifyCode();
      }
      return;
    }
    setLoading(true);

    try {
      const endpoint = isLogin ? "/auth/login" : "/auth/register";
      let payload = isLogin
        ? { email: formData.email, password: formData.password }
        : { ...formData, language: lang }; // Add current language to registration

      // For registration, get reCAPTCHA token
      if (!isLogin) {
        try {
          if (!recaptchaLoaded || typeof window.grecaptcha === 'undefined') {
            throw new Error('reCAPTCHA not loaded');
          }
          
          const recaptchaToken = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'register' });
          payload = { ...payload, recaptcha_token: recaptchaToken };
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          toast.error(t('auth.recaptchaError') || 'reCAPTCHA verification failed. Please try again.');
          setLoading(false);
          return;
        }
      }

      const response = await axios.post(`${API}${endpoint}`, payload);
      
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      if (!isLogin && typeof window.fbq === 'function') {
        window.fbq('track', 'Lead');
      }
      
      toast.success(isLogin ? t('auth.loginSuccess') : t('auth.registerSuccess'));
      setIsAuthenticated(true);
      
      // Auto-apply promo code from ?promo= link after registration
      if (!isLogin && pendingPromo) {
        try {
          const authHeader = { headers: { Authorization: `Bearer ${response.data.access_token}` } };
          const v = await axios.post(`${API}/promocodes/validate`, { code: pendingPromo, plan: 'starter' });
          if (v.data.promo_type === 'free_months') {
            const r = await axios.post(`${API}/promocodes/redeem`, { code: pendingPromo }, authHeader);
            toast.success(r.data.message, { duration: 8000 });
            localStorage.removeItem('chatity_promo');
          } else {
            localStorage.setItem(`promocode_${response.data.user.id}`, JSON.stringify(v.data));
            localStorage.removeItem('chatity_promo');
            toast.success(
              lang === 'fr'
                ? `Code promo ${pendingPromo} appliqué : -${v.data.discount_percent}% au paiement`
                : `Promo code ${pendingPromo} applied: ${v.data.discount_percent}% off at checkout`,
              { duration: 8000 }
            );
          }
        } catch (promoErr) {
          console.warn('Promo auto-apply failed:', promoErr.response?.data?.detail);
          // keep chatity_promo so the onboarding step 2 can retry it
        }
      }
      
      // Redirect based on user role
      if (response.data.user.is_admin === true) {
        navigate('/admin/dashboard');
      } else if (!isLogin) {
        navigate('/onboarding');
      } else {
        navigate('/calendar');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Gradient Background */}
      <AnimatedGradientBackground />
      
      {/* Auth Form Container */}
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white rounded-lg shadow-2xl p-8 border-4 border-black">
          {/* Language Switcher */}
          <div className="flex justify-end gap-2 mb-4">
            <button
              onClick={() => setLanguage('en')}
              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                lang === 'en'
                  ? 'bg-black text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => setLanguage('fr')}
              className={`px-3 py-1 text-sm font-medium rounded transition-colors ${
                lang === 'fr'
                  ? 'bg-black text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              FR
            </button>
          </div>

          <div className="text-center mb-8">
            <h1 className="mb-2 flex justify-center" data-testid="auth-title">
              <img src="/logo-header.png" alt="Chatity" className="h-10 w-auto" />
            </h1>
            <p className="text-gray-600 text-sm">
              {t('auth.subtitle')}
            </p>
          </div>

          {pendingPromo && !isLogin && (
            <div data-testid="pending-promo-banner" className="mb-6 bg-yellow-50 border-2 border-yellow-400 rounded-md px-4 py-3 text-sm text-center font-medium text-yellow-900">
              🎁 {lang === 'fr'
                ? <>Le code promo <b className="text-yellow-950">{pendingPromo}</b> sera appliqué après l'inscription</>
                : <>Promo code <b className="text-yellow-950">{pendingPromo}</b> will be applied after sign-up</>}
            </div>
          )}

          <div className="flex gap-2 mb-6">
            <Button
              data-testid="login-tab-button"
              onClick={() => setIsLogin(true)}
              className={`flex-1 font-medium ${
                isLogin
                  ? "bg-black text-white hover:bg-gray-800"
                  : "bg-white text-black border-2 border-black hover:bg-gray-100"
              }`}
            >
              {t('auth.login')}
            </Button>
            <Button
              data-testid="register-tab-button"
              onClick={() => setIsLogin(false)}
              className={`flex-1 font-medium ${
                !isLogin
                  ? "bg-black text-white hover:bg-gray-800"
                  : "bg-white text-black border-2 border-black hover:bg-gray-100"
              }`}
            >
              {t('auth.register')}
            </Button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-black font-medium">
                {t('auth.email')}
              </Label>
              <Input
                data-testid="email-input"
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black"
                placeholder="your@email.com"
              />
            </div>

            {isLogin && loginMode === 'code' && codeSent && (
              <div>
                <Label htmlFor="login-code" className="text-black font-medium">
                  {t('auth.enterCode') || 'Код из письма'}
                </Label>
                <Input
                  data-testid="login-code-input"
                  id="login-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={loginCode}
                  onChange={(e) => setLoginCode(e.target.value)}
                  required
                  className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black text-center text-2xl tracking-widest"
                  placeholder="••••••"
                />
                <p className="text-xs text-gray-600 mt-2">
                  {t('auth.codeSentHint') || 'Код и кнопка входа отправлены на почту (проверьте папку Спам)'}
                </p>
              </div>
            )}

            {(!isLogin || loginMode === 'password') && (
            <div>
              <Label htmlFor="password" className="text-black font-medium">
                {t('auth.password')}
              </Label>
              <div className="relative">
                <Input
                  data-testid="password-input"
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black pr-10"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-black mt-0.5"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            )}

            {!isLogin && (
              <>
                <div>
                  <Label htmlFor="salon_name" className="text-black font-medium">
                    {t('auth.companyName')}
                  </Label>
                  <Input
                    data-testid="salon-name-input"
                    id="salon_name"
                    type="text"
                    value={formData.salon_name}
                    onChange={(e) => setFormData({ ...formData, salon_name: e.target.value })}
                    required={!isLogin}
                    className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black"
                    placeholder={t('auth.companyNamePlaceholder')}
                  />
                </div>
                <div>
                  <Label htmlFor="website_url" className="text-black font-medium">
                    {t('auth.companyWebsite')}
                  </Label>
                  <Input
                    data-testid="website-url-input"
                    id="website_url"
                    type="url"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                    className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black"
                    placeholder="https://your-company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="referral_code" className="text-black font-medium">
                    {t('auth.referralCode') || 'Реферальный код (необязательно)'}
                  </Label>
                  <Input
                    data-testid="referral-code-input"
                    id="referral_code"
                    type="text"
                    value={formData.referral_code}
                    onChange={(e) => setFormData({ ...formData, referral_code: e.target.value.trim().toLowerCase() })}
                    className="mt-1 border-2 border-black focus:ring-2 focus:ring-black text-black"
                    placeholder="ivan42"
                  />
                </div>
              </>
            )}

            {!isLogin && (
              <label className="flex items-start gap-2.5 text-sm text-gray-700 cursor-pointer" data-testid="auth-terms-label">
                <input type="checkbox" required checked={!!formData.terms_accepted}
                  onChange={(e) => setFormData({ ...formData, terms_accepted: e.target.checked })}
                  className="w-5 h-5 mt-0.5 border-2 border-black cursor-pointer" data-testid="auth-terms-checkbox" />
                <span>
                  {t('auth.termsAccept')}{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" className="underline font-medium">{t('auth.termsLink')}</a>,{' '}
                  <a href="/dpa" target="_blank" rel="noreferrer" className="underline font-medium">DPA</a>{' '}{t('auth.termsAnd')}{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="underline font-medium">{t('auth.privacyLink')}</a>
                </span>
              </label>
            )}

            <Button
              data-testid="auth-submit-button"
              type="submit"
              disabled={loading || (!isLogin && !formData.terms_accepted)}
              className="w-full bg-black text-white hover:bg-gray-800 font-medium py-6 text-lg border-2 border-black transition-all hover:scale-105"
            >
              {loading
                ? t('auth.loading')
                : isLogin
                ? loginMode === 'code'
                  ? codeSent
                    ? (t('auth.verifyCode') || 'Войти')
                    : (t('auth.sendCode') || 'Получить код входа')
                  : t('auth.loginButton')
                : t('auth.registerButton')}
            </Button>
            
            {isLogin && (
              <div className="text-center mt-4 space-y-2">
                {loginMode === 'code' ? (
                  <>
                    {codeSent && (
                      <button
                        type="button"
                        data-testid="resend-code-btn"
                        onClick={handleRequestCode}
                        className="text-sm text-gray-600 hover:text-black underline block mx-auto"
                      >
                        {t('auth.resendCode') || 'Отправить код ещё раз'}
                      </button>
                    )}
                    <button
                      type="button"
                      data-testid="switch-password-mode-btn"
                      onClick={() => { setLoginMode('password'); setCodeSent(false); setLoginCode(''); }}
                      className="text-sm text-gray-600 hover:text-black underline block mx-auto"
                    >
                      {t('auth.loginWithPassword') || 'Войти по паролю'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid="switch-code-mode-btn"
                      onClick={() => setLoginMode('code')}
                      className="text-sm text-gray-600 hover:text-black underline block mx-auto"
                    >
                      {t('auth.loginWithCode') || 'Войти по коду из письма (без пароля)'}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-sm text-gray-600 hover:text-black underline block mx-auto"
                    >
                      {t('auth.forgotPassword')}
                    </button>
                  </>
                )}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}